import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtINR, fmtDate, daysLabel, STATUS_COLORS, bucketBadge } from "../lib/format";
import RechargeModal from "../components/RechargeModal";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Zap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value, mono }) {
  return <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-sm text-slate-500">{label}</span><span className={`text-sm font-medium ${mono ? "font-mono-num" : ""}`}>{value ?? "—"}</span></div>;
}

export default function CircuitDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [recharge, setRecharge] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get(`/circuits/${id}`); setC(data); }, [id]);
  useEffect(() => { load(); }, [load]);

  const doVerify = async () => { await api.post(`/circuits/${id}/verify`, {}); toast.success("Verified"); load(); };

  if (!c) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="text-sm text-slate-500 flex items-center gap-1 hover:text-slate-800"><ArrowLeft size={16} /> Back</button>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-head text-2xl font-extrabold">{c.customer_name}</h1>
            <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[c.status] || "bg-slate-100"}`}>{c.status}</span>
            {c.bucket && <span className={`text-xs font-bold px-2 py-0.5 rounded ${bucketBadge(c.bucket)}`}>{daysLabel(c.days_remaining)}</span>}
          </div>
          <p className="text-slate-500 text-sm mt-1 font-mono-num">{c.internal_circuit_id} · {c.vendor_name} · {c.service_type}</p>
        </div>
        <div className="flex gap-2">
          {c.verification_pending && can("verify") && <button onClick={doVerify} data-testid="detail-verify" className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2"><CheckCircle2 size={16} /> Mark Verified</button>}
          {c.status === "Active" && can("recharge") && <button onClick={() => setRecharge(true)} data-testid="detail-recharge" className="h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold flex items-center gap-2"><Zap size={16} /> {c.billing_type === "Postpaid" ? "Pay" : "Recharge"}</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-head font-bold mb-2">Billable Item Details</h3>
          <Row label="Location" value={`${c.location_name || "—"}${c.city ? ", " + c.city : ""}`} />
          <Row label="ISP Account No." value={c.isp_account_number} mono />
          <Row label="ISP Circuit ID" value={c.isp_circuit_id} mono />
          <Row label="Plan / Bandwidth" value={c.plan_name || c.bandwidth} />
          <Row label="Static IP" value={c.static_ip} mono />
          <Row label="Billing" value={`${c.billing_type} · ${c.billing_frequency || "—"}`} />
          <Row label="Amount" value={fmtINR(c.amount)} mono />
          <Row label="Registered Mobile" value={c.registered_mobile} mono />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="font-head font-bold mb-2">Current Renewal</h3>
          <Row label="Current Expiry / Due" value={fmtDate(c.next_action_date)} mono />
          <Row label="Renewal Cycle" value={c.renewal_cycle || "—"} />
          <Row label="Days Remaining" value={daysLabel(c.days_remaining)} />
          <Row label="Expected Recharge" value={fmtINR(c.amount)} mono />
          <Row label="Next Expiry (after payment)" value={c.next_expiry_preview ? fmtDate(c.next_expiry_preview) : "Manual / set at payment"} mono />
          <Row label="Renewal Control" value={c.renewal_controlled ? "CONTROLLED" : `INCOMPLETE (${(c.renewal_control_reasons || []).join(", ")})`} />
          <Row label="Last Recharge" value={fmtDate(c.last_recharge_date)} mono />
          <Row label="Assigned Accounts" value={c.assigned_accounts_name} />
          <Row label="Assigned Ops" value={c.assigned_ops_name} />
          <Row label="Verification" value={c.verification_pending ? "Pending" : "Verified"} />
          {c.data_quality_flags?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{c.data_quality_flags.map((f) => <span key={f} className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-700">{f.replace(/_/g, " ")}</span>)}</div>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-head font-bold mb-3">Renewal History (Cycles)</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] uppercase text-slate-500"><th className="py-2">Cycle</th><th className="py-2">Due Date</th><th className="py-2">Paid / Completed</th><th className="py-2">Status</th><th className="py-2">Late</th></tr></thead>
          <tbody>
            {c.renewal_schedules?.map((s) => (
              <tr key={s.id} className="border-b border-slate-100"><td className="py-2 font-mono-num text-xs">#{s.cycle_number}</td><td className="py-2 font-mono-num text-xs">{fmtDate(s.due_date)}</td><td className="py-2 font-mono-num text-xs">{fmtDate(s.completed_date)}</td><td className="py-2"><span className={`text-xs px-2 py-0.5 rounded ${s.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : s.status === "PENDING" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{s.status}</span></td><td className="py-2">{s.was_late ? <span className="text-xs text-red-600">{s.days_late}d</span> : <span className="text-xs text-slate-300">—</span>}</td></tr>
            ))}
            {(!c.renewal_schedules || c.renewal_schedules.length === 0) && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No renewal cycles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-head font-bold mb-3">Recharge / Payment Ledger</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-[11px] uppercase text-slate-500"><th className="py-2">Date</th><th className="py-2 text-right">Amount</th><th className="py-2">Method</th><th className="py-2">Reference</th><th className="py-2">By</th><th className="py-2">Late</th></tr></thead>
          <tbody>
            {c.recharge_transactions?.map((t) => (
              <tr key={t.id} className="border-b border-slate-100"><td className="py-2 font-mono-num text-xs">{fmtDate(t.recharge_date)}</td><td className="py-2 text-right font-mono-num">{fmtINR(t.amount)}</td><td className="py-2 text-xs">{t.payment_method || "—"}</td><td className="py-2 font-mono-num text-xs">{t.reference_number || "—"}</td><td className="py-2 text-xs">{t.user_name}</td><td className="py-2">{t.was_late ? <span className="text-xs text-red-600">{t.days_late}d</span> : <span className="text-xs text-emerald-600">✓</span>}</td></tr>
            ))}
            {(!c.recharge_transactions || c.recharge_transactions.length === 0) && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No transactions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-head font-bold mb-3">Audit Trail</h3>
        <div className="space-y-2">
          {c.audit?.map((a) => (
            <div key={a.id} className="text-xs flex gap-3 border-b border-slate-100 pb-2"><span className="font-mono-num text-slate-400 whitespace-nowrap">{new Date(a.created_at).toLocaleString("en-IN")}</span><span className="font-semibold">{a.action}</span><span className="text-slate-500">by {a.user_name || "—"}</span></div>
          ))}
          {(!c.audit || c.audit.length === 0) && <div className="text-slate-400 text-sm">No audit entries.</div>}
        </div>
      </div>

      {recharge && <RechargeModal circuit={c} onClose={() => setRecharge(false)} onDone={load} />}
    </div>
  );
}
